import React, { useEffect, useState } from 'react';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

/**
 * WaitConfigContent - Configuration form for wait/delay steps
 * 
 * Stores both delaySeconds AND waitUnit/waitValue for proper UI display.
 * This decouples the display label from the raw execution value.
 * 
 * @param {number} delaySeconds - The delay in seconds (for execution)
 * @param {string} waitUnit - The display unit ('days', 'hours', 'minutes')
 * @param {number} waitValue - The display value (e.g., 1 for "1 Day")
 * @param {Function} onChange - Callback with updated config { delaySeconds, waitUnit, waitValue }
 */
const WaitConfigContent = ({
    delaySeconds = SECONDS_PER_DAY,
    waitUnit: initialUnit,
    waitValue: initialValue,
    onChange
}) => {
    const [value, setValue] = useState('1');
    const [unit, setUnit] = useState('days');

    // Initialize from props - prefer explicit waitUnit/waitValue if provided
    useEffect(() => {
        if (initialUnit && initialValue !== undefined) {
            // Use explicit display values if provided
            setUnit(initialUnit);
            setValue(String(initialValue));
        } else {
            // Derive from delaySeconds for backward compatibility
            const parsed = parseInt(delaySeconds, 10);
            const safeSeconds = Number.isFinite(parsed) ? parsed : SECONDS_PER_DAY;

            if (safeSeconds % SECONDS_PER_DAY === 0 && safeSeconds >= SECONDS_PER_DAY) {
                setUnit('days');
                setValue(String(safeSeconds / SECONDS_PER_DAY));
            } else if (safeSeconds % SECONDS_PER_HOUR === 0 && safeSeconds >= SECONDS_PER_HOUR) {
                setUnit('hours');
                setValue(String(safeSeconds / SECONDS_PER_HOUR));
            } else if (safeSeconds % SECONDS_PER_MINUTE === 0) {
                setUnit('minutes');
                setValue(String(safeSeconds / SECONDS_PER_MINUTE));
            } else {
                setUnit('minutes');
                setValue(String(Math.max(1, Math.round(safeSeconds / SECONDS_PER_MINUTE))));
            }
        }
    }, [delaySeconds, initialUnit, initialValue]);

    const unitToSeconds = (selectedUnit) => {
        if (selectedUnit === 'days') return SECONDS_PER_DAY;
        if (selectedUnit === 'hours') return SECONDS_PER_HOUR;
        return SECONDS_PER_MINUTE;
    };

    // Emit change with all config values for display and execution
    const emitChange = (newValue, newUnit) => {
        const numeric = Number(newValue);
        if (!Number.isFinite(numeric)) return;
        const seconds = Math.max(0, numeric) * unitToSeconds(newUnit);
        if (onChange) {
            onChange({
                delaySeconds: seconds,
                waitUnit: newUnit,
                waitValue: numeric,
            });
        }
    };

    const handleValueChange = (e) => {
        const nextValue = e.target.value;
        setValue(nextValue);
        emitChange(nextValue, unit);
    };

    const handleUnitChange = (e) => {
        const nextUnit = e.target.value;
        setUnit(nextUnit);
        emitChange(value, nextUnit);
    };

    return (
        <>
            <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-3">Wait For</label>
                <div className="flex gap-2">
                    <input
                        type="number"
                        value={value}
                        onChange={handleValueChange}
                        className="w-20 bg-white border border-notion-border rounded-lg px-3 py-2.5 text-sm text-center font-bold outline-none"
                    />
                    <select
                        value={unit}
                        onChange={handleUnitChange}
                        className="flex-1 bg-white border border-notion-border rounded-lg px-3 py-2.5 text-sm outline-none"
                    >
                        <option value="days">Days</option>
                        <option value="hours">Hours</option>
                        <option value="minutes">Minutes</option>
                    </select>
                </div>
            </div>
        </>
    );
};

export default WaitConfigContent;
